import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Calendar,
  Clock,
  FileText,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { Formalite } from "../services/baserowAPI";
import { DataManagementSection } from "./DataManagementSection";

interface DashboardProps {
  data: Formalite[];
  loading?: boolean;
  onDataUpdated?: () => void;
  onNavigateToDeadline?: (filter: {
    entreprise?: string;
    client?: string;
    type?: string;
  }) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ data, loading, onDataUpdated, onNavigateToDeadline }) => {
  // Couleurs pour les graphiques
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884D8",
    "#82CA9D",
    "#FFC658",
    "#FF7C7C",
  ];

  // Composant personnalisé pour les labels du camembert
  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, name, percent } = props;
    const percentage = `${(percent * 100).toFixed(0)}%`;
    const maxLength = 20;
    
    let displayName = name;
    if (name && name.length > maxLength) {
      displayName = name.substring(0, maxLength) + "...";
    }

    return (
      <text
        x={x}
        y={y}
        fill="black"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        <tspan x={x} dy={0}>
          {displayName}
        </tspan>
        <tspan x={x} dy={14}>
          ({percentage})
        </tspan>
      </text>
    );
  };

  // Calcul des statistiques
  const stats = useMemo(() => {
    if (!data.length) return null;

    // Total des formalités
    const total = data.length;

    // Statistiques par type (formality_type est maintenant un array de link_row)
    const byType = data.reduce((acc, item) => {
      if (Array.isArray(item.field_7263) && item.field_7263.length > 0) {
        const typeName = item.field_7263[0]?.value || "Non défini";
        acc[typeName] = (acc[typeName] || 0) + 1;
      } else {
        acc["Non défini"] = (acc["Non défini"] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Statistiques par étape (progression est maintenant un string, pas un select)
    const byStep = data.reduce((acc, item) => {
      const step = item.field_7265 || "Non défini";
      // Ne pas exclure 'Clos' ici, le faire plutôt dans les calculs d'échéances
      acc[step] = (acc[step] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Statistiques par statut d'enregistrement (tax - single select)
    const byRegistration = data.reduce((acc, item) => {
      const registration = item.field_7272?.value || "Non défini";
      acc[registration] = (acc[registration] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Statistiques par client (nombre de clients différents)
    const clientSet = new Set<number>();
    data.forEach((item) => {
      if (Array.isArray(item.field_7260)) {
        item.field_7260.forEach((client: any) => {
          if (client && client.id) clientSet.add(client.id);
        });
      }
    });
    const clientCount = clientSet.size;

    // Statistiques par entreprise (company - link_row)
    const byCompany = data.reduce((acc, item) => {
      if (Array.isArray(item.field_7261) && item.field_7261.length > 0) {
        const company = item.field_7261[0]?.value || "Non défini";
        acc[company] = (acc[company] || 0) + 1;
      } else {
        acc["Non défini"] = (acc["Non défini"] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Statistiques par client (top 10)
    const byClient = data.reduce((acc, item) => {
      if (Array.isArray(item.field_7260)) {
        item.field_7260.forEach((client: any) => {
          if (client && client.value) {
            acc[client.value] = (acc[client.value] || 0) + 1;
          }
        });
      }
      return acc;
    }, {} as Record<string, number>);

    // Échéances proches (dans les 30 jours)
    const now = new Date();
    const thirtyDaysFromNow = new Date(
      now.getTime() + 30 * 24 * 60 * 60 * 1000
    );
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    // ÉCHÉANCES AUTOMATIQUES (field_7269)
    const weekDeadlinesAuto = data.filter((item) => {
      if (!item.field_7269) return false;
      if (item.field_7265 === 'Clos') return false;
      const deadline = new Date(item.field_7269);
      return deadline >= now && deadline <= sevenDaysFromNow;
    }).length;

    const upcomingDeadlinesAuto = data.filter((item) => {
      if (!item.field_7269) return false;
      const deadline = new Date(item.field_7269);
      return deadline >= now && deadline <= thirtyDaysFromNow;
    }).length;

    const overdueDeadlinesAuto = data.filter((item) => {
      if (!item.field_7269) return false;
      const deadline = new Date(item.field_7269);
      return deadline < now;
    }).length;

    // ÉCHÉANCES FORCÉES (field_7270)
    const weekDeadlinesForcedFORCED = data.filter((item) => {
      if (!item.field_7270) return false;
      if (item.field_7265 === 'Clos') return false;
      const deadline = new Date(item.field_7270);
      return deadline >= now && deadline <= sevenDaysFromNow;
    }).length;

    const upcomingDeadlinesForced = data.filter((item) => {
      if (!item.field_7270) return false;
      const deadline = new Date(item.field_7270);
      return deadline >= now && deadline <= thirtyDaysFromNow;
    }).length;

    const overdueDeadlinesForced = data.filter((item) => {
      if (!item.field_7270) return false;
      const deadline = new Date(item.field_7270);
      return deadline < now;
    }).length;

    // ÉCHÉANCES IMPÔTS (field_7271 + 30 jours si statut "Envoyé")
    const weekDeadlinesTax = data.filter((item) => {
      if (!item.field_7271) return false;
      if (item.field_7265 === 'Clos') return false;
      // Vérifier que le statut impôts est "Envoyé"
      if (item.field_7272?.value !== 'Envoyé') return false;
      // Ajouter 30 jours à la date d'échéance impôts
      const baseDate = new Date(item.field_7271);
      const deadline = new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      return deadline >= now && deadline <= sevenDaysFromNow;
    }).length;

    const upcomingDeadlinesTax = data.filter((item) => {
      if (!item.field_7271) return false;
      // Vérifier que le statut impôts est "Envoyé"
      if (item.field_7272?.value !== 'Envoyé') return false;
      // Ajouter 30 jours à la date d'échéance impôts
      const baseDate = new Date(item.field_7271);
      const deadline = new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      return deadline >= now && deadline <= thirtyDaysFromNow;
    }).length;

    const overdueDeadlinesTax = data.filter((item) => {
      if (!item.field_7271) return false;
      // Vérifier que le statut impôts est "Envoyé"
      if (item.field_7272?.value !== 'Envoyé') return false;
      // Ajouter 30 jours à la date d'échéance impôts
      const baseDate = new Date(item.field_7271);
      const deadline = new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      return deadline < now;
    }).length;

    // TOTALS (pour compatibilité avec l'ancien code)
    const weekDeadlines = weekDeadlinesAuto + weekDeadlinesForcedFORCED + weekDeadlinesTax;
    const upcomingDeadlines = upcomingDeadlinesAuto + upcomingDeadlinesForced + upcomingDeadlinesTax;
    const overdueDeadlines = overdueDeadlinesAuto + overdueDeadlinesForced + overdueDeadlinesTax;

    return {
      total,
      byType,
      byStep,
      byRegistration,
      byCompany,
      byClient,
      clientCount,
      weekDeadlines,
      upcomingDeadlines,
      overdueDeadlines,
      weekDeadlinesAuto,
      upcomingDeadlinesAuto,
      overdueDeadlinesAuto,
      weekDeadlinesForcedFORCED,
      upcomingDeadlinesForced,
      overdueDeadlinesForced,
      weekDeadlinesTax,
      upcomingDeadlinesTax,
      overdueDeadlinesTax,
    };
  }, [data]);

  // Données pour les graphiques
  const chartData = useMemo(() => {
    if (!stats) return { typeData: [], stepData: [], clientData: [] };

    // Top 5 types
    const typeData = Object.entries(stats.byType)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    // Étapes (déjà filtrées sans 'Clos')
    const stepData = Object.entries(stats.byStep)
      .filter(([name]) => name !== 'Terminé' && name !== 'Clos')
      .map(([name, value]) => ({
        name,
        value,
      }));

    // Top 10 clients (exclure 'Non défini' et noms vides)
    const clientData = Object.entries(stats.byClient)
      .filter(([name]) => name && name !== "Non défini")
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({ name, value }));

    return { typeData, stepData, clientData };
  }, [stats]);

  // Calcul des échéances à venir
  const deadlines = useMemo(() => {
    if (!data.length) return [];

    const now = new Date();
    const items: Array<{
      id: number;
      entreprise: string;
      client: string;
      type: string;
      echeance: Date;
      filterValue: string;
      daysUntil: number;
      urgency: 'overdue' | 'urgent' | 'warning' | 'normal';
    }> = [];

    data.forEach(item => {
      if (!item.field_7270) return;
      if (item.field_7265 === 'Clos') return;

      const echeance = new Date(item.field_7270);
      const diffTime = echeance.getTime() - now.getTime();
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (daysUntil < 7) {
        let urgency: 'overdue' | 'urgent' | 'warning' | 'normal' = 'normal';
        if (daysUntil < 0) {
          urgency = 'overdue';
        } else if (daysUntil === 0) {
          urgency = 'urgent';
        } else if (daysUntil <= 3) {
          urgency = 'warning';
        } else {
          urgency = 'normal';
        }
        items.push({
          id: item.id,
          entreprise: (Array.isArray(item.field_7261) && item.field_7261.length > 0) 
            ? item.field_7261[0]?.value || 'Non défini' 
            : 'Non défini',
          client: (Array.isArray(item.field_7260) && item.field_7260.length > 0) 
            ? item.field_7260[0]?.value || 'Non défini' 
            : 'Non défini',
          type: (Array.isArray(item.field_7263) && item.field_7263.length > 0) 
            ? item.field_7263[0]?.value || 'Non défini' 
            : 'Non défini',
          echeance,
          filterValue: item.field_7270,
          daysUntil,
          urgency
        });
      }
    });

    return items.sort((a, b) => {
      if (a.daysUntil === 0 && b.daysUntil !== 0) return -1;
      if (a.daysUntil !== 0 && b.daysUntil === 0) return 1;
      return a.echeance.getTime() - b.echeance.getTime();
    });
  }, [data]);

  // Calcul des échéances automatiques
  const deadlinesAuto = useMemo(() => {
    if (!data.length) return [];

    const now = new Date();
    const items: Array<{
      id: number;
      entreprise: string;
      client: string;
      type: string;
      echeance: Date;
      filterValue: string;
      daysUntil: number;
      urgency: 'overdue' | 'urgent' | 'warning' | 'normal';
    }> = [];

    data.forEach(item => {
      if (!item.field_7269) return;
      if (item.field_7265 === 'Clos') return;

      const echeance = new Date(item.field_7269);
      const diffTime = echeance.getTime() - now.getTime();
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (daysUntil < 30) {
        let urgency: 'overdue' | 'urgent' | 'warning' | 'normal' = 'normal';
        if (daysUntil < 0) {
          urgency = 'overdue';
        } else if (daysUntil === 0) {
          urgency = 'urgent';
        } else if (daysUntil <= 3) {
          urgency = 'warning';
        } else if (daysUntil <= 7) {
          urgency = 'warning';
        } else {
          urgency = 'normal';
        }
        items.push({
          id: item.id,
          entreprise: (Array.isArray(item.field_7261) && item.field_7261.length > 0) 
            ? item.field_7261[0]?.value || 'Non défini' 
            : 'Non défini',
          client: (Array.isArray(item.field_7260) && item.field_7260.length > 0) 
            ? item.field_7260[0]?.value || 'Non défini' 
            : 'Non défini',
          type: (Array.isArray(item.field_7263) && item.field_7263.length > 0) 
            ? item.field_7263[0]?.value || 'Non défini' 
            : 'Non défini',
          echeance,
          filterValue: item.field_7269,
          daysUntil,
          urgency
        });
      }
    });

    return items.sort((a, b) => a.echeance.getTime() - b.echeance.getTime());
  }, [data]);

  // Calcul des échéances forcées
  const deadlinesForced = useMemo(() => {
    if (!data.length) return [];

    const now = new Date();
    const items: Array<{
      id: number;
      entreprise: string;
      client: string;
      type: string;
      echeance: Date;
      filterValue: string;
      daysUntil: number;
      urgency: 'overdue' | 'urgent' | 'warning' | 'normal';
    }> = [];

    data.forEach(item => {
      if (!item.field_7270) return;
      if (item.field_7265 === 'Clos') return;

      const echeance = new Date(item.field_7270);
      const diffTime = echeance.getTime() - now.getTime();
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (daysUntil < 30) {
        let urgency: 'overdue' | 'urgent' | 'warning' | 'normal' = 'normal';
        if (daysUntil < 0) {
          urgency = 'overdue';
        } else if (daysUntil === 0) {
          urgency = 'urgent';
        } else if (daysUntil <= 3) {
          urgency = 'warning';
        } else if (daysUntil <= 7) {
          urgency = 'warning';
        } else {
          urgency = 'normal';
        }
        items.push({
          id: item.id,
          entreprise: (Array.isArray(item.field_7261) && item.field_7261.length > 0) 
            ? item.field_7261[0]?.value || 'Non défini' 
            : 'Non défini',
          client: (Array.isArray(item.field_7260) && item.field_7260.length > 0) 
            ? item.field_7260[0]?.value || 'Non défini' 
            : 'Non défini',
          type: (Array.isArray(item.field_7263) && item.field_7263.length > 0) 
            ? item.field_7263[0]?.value || 'Non défini' 
            : 'Non défini',
          echeance,
          filterValue: item.field_7270,
          daysUntil,
          urgency
        });
      }
    });

    return items.sort((a, b) => a.echeance.getTime() - b.echeance.getTime());
  }, [data]);

  // Calcul des échéances impôts
  const deadlinesTax = useMemo(() => {
    if (!data.length) return [];

    const now = new Date();
    const items: Array<{
      id: number;
      entreprise: string;
      client: string;
      type: string;
      echeance: Date;
      filterValue: string;
      daysUntil: number;
      urgency: 'overdue' | 'urgent' | 'warning' | 'normal';
    }> = [];

    data.forEach(item => {
      if (!item.field_7271) return;
      if (item.field_7265 === 'Clos') return;
      // Vérifier que le statut impôts est "Envoyé"
      if (item.field_7272?.value !== 'Envoyé') return;

      // Ajouter 30 jours à la date d'échéance impôts
      const baseDate = new Date(item.field_7271);
      const echeance = new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      const diffTime = echeance.getTime() - now.getTime();
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (daysUntil < 30) {
        let urgency: 'overdue' | 'urgent' | 'warning' | 'normal' = 'normal';
        if (daysUntil < 0) {
          urgency = 'overdue';
        } else if (daysUntil === 0) {
          urgency = 'urgent';
        } else if (daysUntil <= 3) {
          urgency = 'warning';
        } else if (daysUntil <= 7) {
          urgency = 'warning';
        } else {
          urgency = 'normal';
        }
        items.push({
          id: item.id,
          entreprise: (Array.isArray(item.field_7261) && item.field_7261.length > 0) 
            ? item.field_7261[0]?.value || 'Non défini' 
            : 'Non défini',
          client: (Array.isArray(item.field_7260) && item.field_7260.length > 0) 
            ? item.field_7260[0]?.value || 'Non défini' 
            : 'Non défini',
          type: (Array.isArray(item.field_7263) && item.field_7263.length > 0) 
            ? item.field_7263[0]?.value || 'Non défini' 
            : 'Non défini',
          echeance,
          filterValue: item.field_7271,
          daysUntil,
          urgency
        });
      }
    });

    return items.sort((a, b) => a.echeance.getTime() - b.echeance.getTime());
  }, [data]);

  // Fonction pour formater la date en français
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fonction pour obtenir le texte de délai
  const getDelayText = (daysUntil: number): string => {
    if (daysUntil < 0) {
      const daysPast = Math.abs(daysUntil);
      return `En retard de ${daysPast} jour${daysPast > 1 ? 's' : ''}`;
    } else if (daysUntil === 0) {
      return "Aujourd'hui";
    } else if (daysUntil === 1) {
      return "Demain";
    } else {
      return `Dans ${daysUntil} jour${daysUntil > 1 ? 's' : ''}`;
    }
  };

  // Fonction pour obtenir l'icône et les couleurs selon l'urgence
  const getUrgencyStyle = (urgency: 'overdue' | 'urgent' | 'warning' | 'normal') => {
    switch (urgency) {
      case 'overdue':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-700',
          iconColor: 'text-red-500'
        };
      case 'urgent':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-700',
          iconColor: 'text-orange-500'
        };
      case 'warning':
        return {
          icon: Clock,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-700',
          iconColor: 'text-yellow-500'
        };
      case 'normal':
        return {
          icon: Calendar,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-700',
          iconColor: 'text-blue-500'
        };
    }
  };

  // Fonction pour rendre une liste d'échéances
  const renderDeadlinesList = (
    title: string,
    deadlinesList: typeof deadlinesAuto,
    _filterField: 'field_7269' | 'field_7270' | 'field_7271',
    bgColor: string,
    borderColor: string
  ) => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

        {deadlinesList.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-sm">Aucune échéance à venir (30 jours)</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deadlinesList.map((deadline) => {
              const style = getUrgencyStyle(deadline.urgency);
              const Icon = style.icon;

              return (
                <div
                  key={deadline.id}
                  onClick={() =>
                    onNavigateToDeadline?.({
                      entreprise: deadline.entreprise,
                      client: deadline.client,
                      type: deadline.type,
                    })
                  }
                  className={`border rounded-lg p-4 ${style.bgColor} ${style.borderColor} cursor-pointer hover:opacity-75 transition-opacity`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon className={`h-5 w-5 mt-0.5 ${style.iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-medium ${style.textColor}`}>
                          {deadline.entreprise}
                        </p>
                        <span className={`text-xs font-medium ${style.textColor}`}>
                          {getDelayText(deadline.daysUntil)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Client:</span> {deadline.client}
                        {' • '}
                        <span className="font-medium">Type:</span> {deadline.type}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Échéance: {formatDate(deadline.echeance)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-8 text-center text-gray-500">
        Aucune donnée disponible pour les statistiques
      </div>
    );
  }

  return (
    <div className="space-y-8 mb-8">
      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total des formalités */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Formalités
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.total}
              </p>
            </div>
          </div>
        </div>

        {/* Nombre de clients différents */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Clients</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.clientCount}
              </p>
            </div>
          </div>
        </div>

        {/* Total Échéances */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Échéances (30j)
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.upcomingDeadlines}
              </p>
            </div>
          </div>
        </div>

        {/* Échéances dépassées */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Échéances dépassées
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.overdueDeadlines}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Détail par type d'échéances */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {renderDeadlinesList(
          "📅 Échéances Automatiques",
          deadlinesAuto,
          'field_7269',
          "from-blue-50 to-blue-100",
          "border-blue-200"
        )}
        {renderDeadlinesList(
          "⚡ Échéances Forcées",
          deadlinesForced,
          'field_7270',
          "from-orange-50 to-orange-100",
          "border-orange-200"
        )}
        {renderDeadlinesList(
          "📊 Échéances Impôts",
          deadlinesTax,
          'field_7271',
          "from-green-50 to-green-100",
          "border-green-200"
        )}
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Répartition par type (top 5) */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Répartition par type (Top 5)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.typeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.typeData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Répartition par étape (sans 'Clos') */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Répartition par Étape
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.stepData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Section de gestion des données */}
      <DataManagementSection onDataUpdated={onDataUpdated} />

      {/* Top clients */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Top 10 Clients
        </h3>
        {chartData.clientData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucun client à afficher
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData.clientData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#82ca9d" />
              
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
